import React, {
  useState, useCallback, useEffect, useRef, useMemo,
} from 'react'
import { connect } from 'react-redux'
import { SearchContext } from '../../services/search/SearchContext'
import { SupplementarySearchContext } from '../../services/search/SupplementarySearchContext'
import { reportLocalVsAlgolia } from '../../services/search/searchReporter'
import AlgoliaSearchProvider from '../../services/search/providers/AlgoliaSearchProvider'
import MiniSearchContentProvider from '../../services/search/providers/MiniSearchContentProvider'
import NavigationMiniSearchProvider from '../../services/search/providers/NavigationMiniSearchProvider'
import ExternalLinksMiniSearchProvider from '../../services/search/providers/ExternalLinksMiniSearchProvider'
import QuickActionsMiniSearchProvider from '../../services/search/providers/QuickActionsMiniSearchProvider'
import {
  DEFAULT_LIMIT_PER_INDEX,
  SEARCH_PROFILES,
} from '../../services/search/local/buildMiniSearchIndices'
import { PRESENTATION_ENGINES } from '../../services/search/presentationEngine'

// ─── Provider role config ──────────────────────────────────────────────────────
// Extend this list to add/remove providers without changing any consumer code.
// role: 'display'  → results populate SearchContext (shown to the user)
// role: 'shadow'   → results sent to searchReporter only (quality assessment)
// role: 'additive' → results populate SupplementarySearchContext (supplementary UI slots)

// ─── Local search config ───────────────────────────────────────────────────────
// Two MiniSearch content providers run over the same indices — one per search
// profile — so every query is scored under both profiles against the same
// Algolia results (quality assessment via searchReporter). Normally both are
// shadows and Algolia is displayed; see DISPLAY_ENGINE below.
//
// Non-content registries (navigation / external-help / quick-actions) run on
// MiniSearch and land in SupplementarySearchContext. PRESENTATION_ENGINE is what
// consumers resolve provider ids through (via localProviderId), so it stays as
// the seam even though only one engine remains.
const PRESENTATION_ENGINE = PRESENTATION_ENGINES.MINISEARCH

// Which engine's results reach the user.
//
// Driven by REACT_APP_SEARCH_DISPLAY_ENGINE ('algolia' default | 'minisearch')
// so the Algolia cutoff is a config value and a redeploy — no code change and no
// release branch. Anything other than 'minisearch' keeps Algolia, which makes an
// unset or misspelled value fail towards today's behaviour.
const DISPLAY_ENGINES = { ALGOLIA: 'algolia', MINISEARCH: 'minisearch' }
const CONFIGURED_DISPLAY_ENGINE =
  process.env.REACT_APP_SEARCH_DISPLAY_ENGINE === DISPLAY_ENGINES.MINISEARCH ?
    DISPLAY_ENGINES.MINISEARCH :
    DISPLAY_ENGINES.ALGOLIA

// When true, the settled result set is compared (Algolia vs both local
// engines) and the JSON quality report is emitted (analytics + dev console).
// Driven by REACT_APP_SEARCH_QUALITY_REPORTING so reporting can be switched off
// per stage without a code change; anything other than 'false' leaves it on.
const REPORTING_ENABLED = process.env.REACT_APP_SEARCH_QUALITY_REPORTING !== 'false'

// Each provider (Algolia display + the MiniSearch content and non-content
// providers) reports its results independently and asynchronously, so the
// result state updates several times as a single query settles. Debounce the
// quality report by this many ms after the LAST update so exactly one report
// is emitted per query (one CDP record per search, not one per provider).
const REPORT_DEBOUNCE_MS = 600

// Hard deadline, measured from the moment the query changed, after which a
// report is emitted even though Algolia never caught up.
//
// The debounce alone is not enough. The local engines are synchronous, so their
// results are always current; Algolia is a network round-trip. If it lands
// later than the debounce, the report used to be built from Algolia's PREVIOUS
// results — or from DEFAULT_RESULTS on the first query of a session — and
// MiniSearch was scored against a result set belonging to a different query.
// That inflated both the local-only-excess cohort and the F1 = 0 bucket.
//
// Correctness now comes from the query-match guard below, not from the timer.
// This deadline exists only so the race is COUNTED rather than silently
// dropped: those rows are still emitted, flagged `algoliaSettled: false`, and
// every decision query filters them out (see Q19/Q20 in
// SEARCH_QUALITY_ANALYTICS.md).
const REPORT_TIMEOUT_MS = 3000

// Which search UI this orchestrator drives. TopNav caps every engine at 4 hits
// while the results page asks for 100, so hit counts are only comparable within
// one surface — and the quality event carried no surface at all until now.
function resolveSurface(isTopNav, isSectionHome) {
  if (isTopNav) return 'topNav'
  if (isSectionHome) return 'sectionHome'
  return 'searchResults'
}

// Algolia can only display if a key was actually issued for it. `GET /search-key`
// failing, or Algolia being switched off entirely, used to leave the user with a
// disabled input; now it falls through to the local engine, which needs no key.
function resolveDisplayEngine(algoliaSearchKey) {
  if (CONFIGURED_DISPLAY_ENGINE === DISPLAY_ENGINES.ALGOLIA && algoliaSearchKey) {
    return DISPLAY_ENGINES.ALGOLIA
  }
  return DISPLAY_ENGINES.MINISEARCH
}

function buildProviderConfig(algoliaSearchKey) {
  const config = []
  const displayEngine = resolveDisplayEngine(algoliaSearchKey)
  // Whichever engine is not displaying becomes a shadow, so the comparison keeps
  // running after the switch — in reverse, but against the same gold standard.
  if (algoliaSearchKey) {
    config.push({
      id: 'algolia',
      role: displayEngine === DISPLAY_ENGINES.ALGOLIA ? 'display' : 'shadow',
      component: AlgoliaSearchProvider,
    })
  }
  // Local content search — the same MiniSearch indices are searched three
  // times, once per profile, and every result set is scored against Algolia.
  // Only the baseline arm can ever display: the two tuned profiles are pure
  // shadows, which carries no product risk and needs no randomisation, since
  // every query is measured under all three at once.
  //
  // Three passes rather than two costs one extra in-memory BM25 search over
  // data that is already loaded. The indices are small — a median library holds
  // 254 sets_questions documents, p95 775 — so the marginal cost is well under
  // a millisecond. See SEARCH_QUALITY_ANALYTICS.md §4g.
  config.push({
    id: 'minisearch-content',
    role: displayEngine === DISPLAY_ENGINES.MINISEARCH ? 'display' : 'shadow',
    label: 'Library (BM25)',
    component: MiniSearchContentProvider,
    profile: SEARCH_PROFILES.BASELINE,
  })
  config.push({
    id: 'minisearch-tuned-content',
    role: 'shadow',
    label: 'Library (BM25 tuned)',
    component: MiniSearchContentProvider,
    profile: SEARCH_PROFILES.TUNED,
  })
  config.push({
    id: 'minisearch-bm25-content',
    role: 'shadow',
    label: 'Library (BM25 tuned, length-normalised)',
    component: MiniSearchContentProvider,
    profile: SEARCH_PROFILES.TUNED_BM25,
  })
  config.push({
    id: 'navigation-minisearch', role: 'additive', label: 'Navigation', component: NavigationMiniSearchProvider,
  })
  config.push({
    id: 'external-help-minisearch', role: 'additive', label: 'Help & External', component: ExternalLinksMiniSearchProvider,
  })
  config.push({
    id: 'quick-actions-minisearch', role: 'additive', label: 'Quick Actions', component: QuickActionsMiniSearchProvider,
  })
  return config
}

// ─── Options builder ───────────────────────────────────────────────────────────

const TOP_NAV_HITS_PER_PAGE = 4
const SECTION_HOME_HITS_PER_PAGE = 200
const CONTENT_SEARCH_FILTERS = 'archived!=1 AND parentArchived!=1'

function buildAlgoliaOptions(isTopNav, isSectionHome, hitsPerPage, algoliaSearchKey) {
  const primaryIndex = isSectionHome ? 'sets_questions' : 'repos'
  // Secondary indexes are queried in parallel for multi-index search.
  // isSectionHome queries only the primary index; all other contexts include
  // both folders and sets_questions alongside repos.
  const secondaryIndexes = isSectionHome ? [] : ['folders', 'sets_questions']
  let resolvedHitsPerPage = hitsPerPage
  if (isTopNav) resolvedHitsPerPage = TOP_NAV_HITS_PER_PAGE
  else if (isSectionHome) resolvedHitsPerPage = SECTION_HOME_HITS_PER_PAGE
  return {
    apiKey: algoliaSearchKey,
    primaryIndex,
    secondaryIndexes,
    hitsPerPage: resolvedHitsPerPage,
    filters: CONTENT_SEARCH_FILTERS,
  }
}

// ─── Default context value ─────────────────────────────────────────────────────

const DEFAULT_INDEX_RESULT = {
  hits: [], hitCount: 0, hasMore: false, loadMore: () => {},
}

const DEFAULT_RESULTS = {
  suggestions: [],
  hits: [],
  indexResults: {
    repos: { ...DEFAULT_INDEX_RESULT },
    folders: { ...DEFAULT_INDEX_RESULT },
    // eslint-disable-next-line camelcase
    sets_questions: { ...DEFAULT_INDEX_RESULT },
  },
}

// ─── SearchOrchestrator ────────────────────────────────────────────────────────

/**
 * Owns query state and renders all search providers as headless siblings.
 * Routes each provider's results to the correct context based on its role.
 *
 * Consumer components (TopNavSearchBar, SearchResultsContainer, etc.) read only
 * from SearchContext or SupplementarySearchContext — they have no knowledge of
 * which providers are active.
 */
function SearchOrchestrator({
  algoliaSearchKey,
  libraryLoadStatus,
  isTopNav,
  isSectionHome,
  hitsPerPage,
  searchState,
  onSearchStateChange,
  children,
}) {
  const [query, setQuery] = useState(() => (searchState && searchState.query) || '')
  // Display results are held together with the query they were fetched for AND
  // the provider that produced them, in a single state object so the three can
  // never be read out of step. The provider id matters because the display role
  // can move between engines mid-session: `algoliaSearchKey` arrives
  // asynchronously, so MiniSearch holds the display role until it lands.
  // Labelling this block with whoever holds the role *now* would hand
  // MiniSearch's own results to the reporter as the Algolia gold standard.
  const [display, setDisplay] = useState({
    providerId: null, query: null, results: DEFAULT_RESULTS,
  })
  const [additiveResults, setAdditiveResults] = useState([])
  const [shadowResults, setShadowResults] = useState([])

  // Sync query when the URL-driven searchState prop changes (e.g. browser back).
  // React bails out of a re-render when setQuery is called with the same value,
  // so there is no risk of an infinite loop with the onSearchStateChange effect.
  useEffect(() => {
    const externalQuery = (searchState && searchState.query) || ''
    setQuery(externalQuery)
  }, [searchState])

  // Notify the parent (e.g. SearchResultsContainer) when the query changes so
  // it can debounce URL updates. Use a ref so onSearchStateChange can change
  // without this effect becoming stale.
  const onSearchStateChangeRef = useRef(onSearchStateChange)
  onSearchStateChangeRef.current = onSearchStateChange
  useEffect(() => {
    if (onSearchStateChangeRef.current) {
      onSearchStateChangeRef.current({ query })
    }
  }, [query])

  // Route results based on the provider's role.
  //
  // Additive blocks carry both the flat `hits` list and the optional
  // `indexResults` object. Providers that mirror Algolia's per-index shape
  // (the MiniSearch content providers) populate `indexResults`; lightweight
  // additive providers (navigation, external-help) leave it undefined and
  // consumers fall back to reading flat `hits`.
  const handleResults = useCallback((role, providerId, results) => {
    if (role === 'display') {
      // Merged onto the default shape so a provider that omits a field cannot
      // hand `undefined` to consumers that read `indexResults.repos.hitCount`.
      setDisplay({
        providerId,
        query: results.query,
        results: {
          ...DEFAULT_RESULTS,
          ...results,
          indexResults: results.indexResults || DEFAULT_RESULTS.indexResults,
        },
      })
    } else if (role === 'additive') {
      setAdditiveResults((prev) => {
        const without = prev.filter((r) => r.providerId !== providerId)
        return [...without, {
          providerId,
          hits: results.hits || [],
          indexResults: results.indexResults,
        }]
      })
    } else if (role === 'shadow') {
      setShadowResults((prev) => {
        const without = prev.filter((r) => r.providerId !== providerId)
        return [...without, {
          providerId,
          query: results.query,
          hits: results.hits || [],
          indexResults: results.indexResults,
          indexSizes: results.indexSizes,
          // Lets the reporter ask the local index whether a document it missed
          // was ever there. Only MiniSearch supplies it.
          hasDocument: results.hasDocument,
          // Size of the filtered remote corpus. Only Algolia supplies it.
          corpus: results.corpus,
        }]
      })
    }
  }, [])

  // Stable references — providers receive the same options object identity
  // until their inputs actually change, preventing cascading re-renders that
  // would re-trigger Algolia's connector HOCs.
  const algoliaOptions = useMemo(
    () => buildAlgoliaOptions(isTopNav, isSectionHome, hitsPerPage, algoliaSearchKey),
    [isTopNav, isSectionHome, hitsPerPage, algoliaSearchKey],
  )
  const providerConfig = useMemo(
    () => buildProviderConfig(algoliaSearchKey),
    [algoliaSearchKey],
  )
  const displayProviderId = useMemo(
    () => (providerConfig.find((p) => p.role === 'display') || {}).id || 'unknown',
    [providerConfig],
  )
  const hasAlgolia = useMemo(
    () => providerConfig.some((p) => p.id === 'algolia'),
    [providerConfig],
  )

  // Memoised per-provider onResults callback. Identity is stable for the
  // lifetime of the orchestrator since handleResults is stable.
  const onResultsForProvider = useCallback(
    (role, id) => (results) => handleResults(role, id, results),
    [handleResults],
  )
  const onResultsCacheRef = useRef(new Map())
  const getOnResults = (role, id) => {
    const key = `${role}:${id}`
    if (!onResultsCacheRef.current.has(key)) {
      onResultsCacheRef.current.set(key, onResultsForProvider(role, id))
    }
    return onResultsCacheRef.current.get(key)
  }

  const searchContextValue = useMemo(
    () => ({
      ...display.results,
      currentRefinement: query,
      refine: setQuery,
    }),
    [display, query],
  )

  const supplementaryContextValue = useMemo(
    () => ({ additiveResults, presentationEngine: PRESENTATION_ENGINE }),
    [additiveResults],
  )

  // Context reported with every quality report so the rows can be sliced
  // without guessing which UI produced them or which cap was in force.
  const surface = useMemo(
    () => resolveSurface(isTopNav, isSectionHome),
    [isTopNav, isSectionHome],
  )
  const limits = useMemo(
    () => ({
      hitsPerPage: algoliaOptions.hitsPerPage,
      minisearchLimitPerIndex: algoliaOptions.hitsPerPage || DEFAULT_LIMIT_PER_INDEX,
    }),
    [algoliaOptions],
  )

  // ─── Search-quality report ───────────────────────────────────────────────
  //
  // Every provider's block, keyed by the id of the provider that produced it and
  // independent of the role it currently holds. The reporter looks Algolia up by
  // id, so which engine is on display no longer changes what gets scored against
  // what — Algolia stays the gold standard whether it is displaying or
  // shadowing.
  //
  // A provider that just moved from display to shadow appears in both lists; the
  // shadow entry is the current one, so it wins and the stale display block is
  // dropped.
  const resultBlocks = useMemo(() => {
    const blocks = [...shadowResults]
    const stillCurrent = blocks.some((b) => b.providerId === display.providerId)
    if (display.providerId && !stillCurrent) {
      blocks.push({
        providerId: display.providerId,
        query: display.query,
        hits: display.results.hits || [],
        indexResults: display.results.indexResults,
        indexSizes: display.results.indexSizes,
        hasDocument: display.results.hasDocument,
        corpus: display.results.corpus,
      })
    }
    return blocks
  }, [display, shadowResults])

  // Algolia is the only asynchronous provider, so it is the only one that can
  // still be holding a previous query's hits. Reporting before it catches up
  // scores MiniSearch against the wrong result set.
  const algoliaQuery = useMemo(() => {
    const block = resultBlocks.find((b) => b.providerId === 'algolia')
    return block ? block.query : null
  }, [resultBlocks])

  // Exactly one report per query, and only ever built from an Algolia result set
  // that actually belongs to that query.
  //
  // Two effects cooperate:
  //   1. the latch effect resets the once-per-query guard and arms the hard
  //      deadline, and re-runs only when the query itself changes;
  //   2. the debounce effect waits for the result burst to settle, and refuses
  //      to schedule anything while Algolia is still holding another query's
  //      results.
  //
  // `emitReportRef` is reassigned on every render so whichever timer fires
  // reads the current results rather than the ones captured when it was armed.
  const reportedQueryRef = useRef(null)
  const latchedQueryRef = useRef(null)
  const emitReportRef = useRef(() => {})
  emitReportRef.current = (reportReason) => {
    reportedQueryRef.current = query
    reportLocalVsAlgolia({
      query,
      displayedProvider: displayProviderId,
      resultBlocks,
      additiveResults,
      // Read from the guard itself rather than inferred from which timer fired.
      // A burst of provider updates can keep re-arming the debounce past the
      // hard deadline, and those rows are settled — flagging them otherwise
      // would drop good measurements from every decision query.
      algoliaSettled: algoliaQuery === query,
      reportReason,
      surface,
      limits,
      loadStatus: libraryLoadStatus,
    })
  }

  // Without Algolia there is no gold standard, so a report would be a row of
  // zeroes that reads like a quality collapse. Stay silent instead.
  const reportingEnabled = REPORTING_ENABLED && hasAlgolia

  useEffect(() => {
    if (!reportingEnabled) return undefined
    if (!query) return undefined
    // Only a genuinely new query reopens the once-per-query guard. This effect
    // also re-runs when reporting turns on mid-session (the Algolia key landing
    // flips `reportingEnabled`), and re-arming there would report twice.
    if (latchedQueryRef.current !== query) {
      latchedQueryRef.current = query
      reportedQueryRef.current = null
    }
    const handle = setTimeout(() => {
      if (reportedQueryRef.current === query) return
      emitReportRef.current('timeout')
    }, REPORT_TIMEOUT_MS)
    return () => clearTimeout(handle)
  }, [query, reportingEnabled])

  useEffect(() => {
    if (!reportingEnabled) return undefined
    if (!query) return undefined
    if (algoliaQuery !== query) return undefined
    if (reportedQueryRef.current === query) return undefined
    const handle = setTimeout(() => {
      if (reportedQueryRef.current === query) return
      emitReportRef.current('settled')
    }, REPORT_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [
    query,
    reportingEnabled,
    algoliaQuery,
    resultBlocks,
    additiveResults,
  ])

  // Renders whenever a display provider exists. MiniSearch needs no key, so
  // this is now always true — the old `if (algoliaSearchKey)` is what made
  // search die whenever the key request failed.
  if (displayProviderId !== 'unknown') {
    return (
      <SearchContext.Provider value={searchContextValue}>
        <SupplementarySearchContext.Provider value={supplementaryContextValue}>
          {providerConfig.map(({
            id, role, profile, component: Provider,
          }) => (
            <Provider
              key={id}
              query={query}
              options={algoliaOptions}
              profile={profile}
              onResults={getOnResults(role, id)}
              displayedResults={display.results}
              displayedProvider={displayProviderId}
            />
          ))}
          {children}
        </SupplementarySearchContext.Provider>
      </SearchContext.Provider>
    )
  }
  if (isTopNav) {
    return <input disabled className='topNav-search' />
  }
  return null
}

function mapStateToProps(state) {
  const { searchKey } = state
  let contentsSearchKey
  if (searchKey) {
    contentsSearchKey = searchKey.contents
  }
  return {
    algoliaSearchKey: contentsSearchKey,
    // What each library collection was supposed to load and whether it did.
    // Reported with every quality report so a missing hit can be attributed.
    libraryLoadStatus: state.libraryLoadStatus,
  }
}

export default connect(
  mapStateToProps,
  {},
)(SearchOrchestrator)
